import React, { useState, useMemo, useEffect } from "react";
import {
  ArrowRight,
  ArrowLeft,
  Building,
  MapPin,
  Package,
  Truck,
  Check,
  File,
  FileInput,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import { MaterialInputAutoComplete } from "features/shared/presentation/components";
import { selectGetStockOrderStores } from "../slices/get-store.slice";
import {
  categoryModel,
  fulfillmentModel,
  selectedStoreModel,
} from "features/stock-ordering/core/domain/store-and-category.model";
import {
  STOCK_ORDER_CATEGORY,
  STOCK_ORDER_FULLFILLMENT,
} from "features/shared/constants";
import { StockOrderProductSelector } from "./stock-order-product-selection";
import { OrderTableData } from "features/stock-ordering/core/domain/order-table-row.model";
import { getMinimumTotalAmountData } from "../slices/get-minimum-total-amount.slice";
import { confirmNewOrder } from "../slices/confirm-new-order.slice";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { selectAdminSettingsEditUserMaterialSupplierCustomer } from "features/admin/presentation/slices/admin-settings-edit-user-meterial-supplier-customer.slice";
import { stockInventoryFetchCurrentStock } from "../slices/stock-inventory-fetch-current-stock.slice";
import { UploadMultipleImageModal } from "../modals";
import {
  closeModalScroll,
  toggleModalScroll,
} from "../slices/modalScrollSlice.slice";
import { createQueryParams } from "features/config/helpers";
import {
  getOrderConfig,
  selectGetOrderConfig,
} from "../slices/get-order-config.slice";

interface PlaceOrderInputModel {
  selectedStore: selectedStoreModel;
  selectedAddress: {
    ship_to_address: string;
    store_id: number | null;
  };
  category: categoryModel;
  selectedSupplier: number | null;
  fulfillment: fulfillmentModel;

  poInfo: {
    poNumber: string;
    uploadedPo: File[] | null;
  };
}

interface StepConfig {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  fieldName: keyof PlaceOrderInputModel;
  isVisible: (
    formState: PlaceOrderInputModel,
    companyId: number,
    selectedSupplier: number | null
  ) => boolean;
}

interface PlaceOrderInputWizardProps {
  openConfirmationState: () => void;
  setSelectedStore: (selectedStoreData: selectedStoreModel) => void;
}

export function PlaceOrderInputWizard(props: PlaceOrderInputWizardProps) {
  const dispatch = useAppDispatch();

  // Redux selectors
  const getStores = useAppSelector(selectGetStockOrderStores);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const placeOrderConfig = useAppSelector(selectGetOrderConfig);
  const adminSettingsEditUserMaterialSupplierCustomerState = useAppSelector(
    selectAdminSettingsEditUserMaterialSupplierCustomer
  );

  // Initial State
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const formStateInitialState: PlaceOrderInputModel = {
    selectedStore: { store_id: "", name: "", franchise_type_id: "" },
    selectedAddress: { ship_to_address: "", store_id: null },
    category: { category_id: "", category_name: "" },
    selectedSupplier: null,
    fulfillment: { fulfillment_id: "", fulfillment_name: "" },
    poInfo: {
      poNumber: "",
      uploadedPo: null,
    },
  };

  // State variables
  const [formState, setFormState] = useState<PlaceOrderInputModel>(
    formStateInitialState
  );
  const [productData, setProductData] = useState<OrderTableData[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const queryParam = createQueryParams({
      supplierId: formState.selectedSupplier,
    });

    dispatch(getOrderConfig(queryParam));
  }, [formState.selectedSupplier]);

  // Data preparation
  const addressOption = () => {
    const company = getAdminSessionState.data?.admin.user_details.company;
    if (company?.company_id === 2) {
      return getStores.data?.address || [];
    } else {
      return [
        {
          ship_to_address: company?.address || "",
          store_id: company?.company_id || null,
        },
      ];
    }
  };

  const supplierOption =
    (adminSettingsEditUserMaterialSupplierCustomerState.data &&
      adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier.filter(
        (comp) =>
          getAdminSessionState.data?.admin.user_details.stockOrderSuppliers.some(
            (supp) => supp.customer_company_id === comp.id
          )
      )) ??
    [];

  const selectedSupplierPreview =
    (adminSettingsEditUserMaterialSupplierCustomerState.data &&
      adminSettingsEditUserMaterialSupplierCustomerState.data?.supplier.find(
        (supp) => supp.id === formState.selectedSupplier
      )?.name) ??
    null;

  const companyPreview =
    getAdminSessionState.data?.admin.user_details.company.name ?? null;

  const companyId =
    getAdminSessionState.data?.admin.user_details.company.company_id || 0;

  // Step Configuration
  const stepConfigs: StepConfig[] = [
    {
      id: "supplier",
      title: "Choose Supplier",
      subtitle: "Select the supplier you want to order f rom",
      icon: Building,
      fieldName: "selectedSupplier",
      isVisible: () => true,
    },
    {
      id: "store",
      title: "Select Store",
      subtitle: "Choose the store for your order",
      icon: Building,
      fieldName: "selectedStore",
      isVisible: (formState, companyId) => companyId === 2,
    },
    {
      id: "address",
      title: "Delivery Address",
      subtitle: "Where should we deliver your order?",
      icon: MapPin,
      fieldName: "selectedAddress",
      isVisible: () => true,
    },
    {
      id: "category",
      title: "Product Category",
      subtitle: "What type of products do you need?",
      icon: Package,
      fieldName: "category",
      isVisible: (formState) => formState.selectedSupplier === 1,
    },

    {
      id: "fulfillment",
      title: "Fulfillment Method",
      subtitle:
        "What type of fulfillment method do you need? Pickup or Delivery",
      icon: Truck,
      fieldName: "fulfillment",
      isVisible: (formState, companyId) => companyId === 1,
    },

    {
      id: "poInfo",
      title: "Setup PO Information",
      subtitle:
        "Specify your PO number and upload the document for verification",
      icon: File,
      fieldName: "poInfo",
      isVisible: (formState, companyId) => companyId === 1,
    },
  ];

  // Get visible steps based on current form state
  const visibleSteps = useMemo(() => {
    return stepConfigs.filter((step) =>
      step.isVisible(formState, companyId, formState.selectedSupplier)
    );
  }, [formState, companyId]);

  // Handlers
  const handleConfirmOrder = (isStaggered?: boolean) => () => {
    dispatch(getMinimumTotalAmountData());

    dispatch(
      confirmNewOrder({
        data: {
          selectedStoreId: formState.selectedStore?.store_id,
          selectedAddress: formState.selectedAddress.ship_to_address as string,
          category: formState.category,
          fulfillment: formState.fulfillment,
          selectedSupplier: formState.selectedSupplier,
          poNumber: formState.poInfo.poNumber,
          uploadedPo: formState.poInfo.uploadedPo?.[0],
          OrderData: productData,
          isStaggered: isStaggered,
        },
      })
    );

    if (formState.selectedSupplier) {
      const params = new URLSearchParams();
      productData.forEach((product) =>
        params.append("prodId[]", product.productId)
      );
      const query = params.toString();

      dispatch(
        stockInventoryFetchCurrentStock({
          query: query,
          supplierId: formState.selectedSupplier,
        })
      );
    }

    props.openConfirmationState();
  };

  const handleReset = () => {
    setFormState(formStateInitialState);
    setActiveStep(0);
    setProductData([]);
  };

  const handleInputOnChange = (name: string, value: any) => {
    setFormState({ ...formState, [name]: value });
  };

  const handleAutoCompleteChange = (name: string, value: any) => {
    if (value === null) {
      return;
    }

    if (name === "selectedStore") {
      props.setSelectedStore(value);
    }
    setActiveStep((prev) => prev + 1);
    handleInputOnChange(name, value);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  // Get data for current step
  const getCurrentStepData = (stepId: string) => {
    switch (stepId) {
      case "supplier":
        return {
          autoCompletePropSetup: {
            options: supplierOption,
            optionLabelName: "name",
            placeholder: "Search for a supplier...",
            value: formState.selectedSupplier,
            onChange: (e: any, val: any) =>
              val !== null &&
              handleAutoCompleteChange("selectedSupplier", val.id),
          },
        };
      case "store":
        return {
          autoCompletePropSetup: {
            options: getStores.data?.stores || [],
            optionLabelName: "name",
            placeholder: "Search for a store...",
            value: formState.selectedStore,
            onChange: (e: any, val: any) =>
              val !== null && handleAutoCompleteChange("selectedStore", val),
          },
        };
      case "address":
        return {
          autoCompletePropSetup: {
            options: addressOption(),
            optionLabelName: "ship_to_address",
            placeholder: "Search for an address...",
            value: formState.selectedAddress,
            onChange: (e: any, val: any) =>
              val !== null && handleAutoCompleteChange("selectedAddress", val),
          },
        };
      case "category":
        return {
          autoCompletePropSetup: {
            options: STOCK_ORDER_CATEGORY,
            optionLabelName: "category_name",
            placeholder: "Select a category...",
            value: formState.category,
            onChange: (e: any, val: any) =>
              val !== null && handleAutoCompleteChange("category", val),
          },
        };

      case "fulfillment":
        return {
          autoCompletePropSetup: {
            options: STOCK_ORDER_FULLFILLMENT,
            optionLabelName: "fulfillment_name",
            placeholder: "Select a fulfillment method...",
            value: formState.fulfillment,
            onChange: (e: any, val: any) =>
              val !== null && handleAutoCompleteChange("fulfillment", val),
          },
        };

      case "poInfo":
        return {
          poInfo: {
            poNumber: {
              placeholder: "Enter PO Number",
              max: 20,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setFormState({
                  ...formState,
                  poInfo: { ...formState.poInfo, poNumber: e.target.value },
                }),
            },
            uploadPo: {
              btnName: "Upload PO",
              onclick: () => {
                setOpenUploadModal(true);
                dispatch(toggleModalScroll());
              },
            },
          },
        };

      default:
        return null;
    }
  };

  const isLastStep = activeStep >= visibleSteps.length;
  const currentStep = visibleSteps[activeStep];
  const currentStepData = currentStep
    ? getCurrentStepData(currentStep.id)
    : null;

  // Progress calculation
  const progress =
    ((activeStep + (isLastStep ? 1 : 0)) / (visibleSteps.length + 1)) * 100;

  // Summary data
  const getSummaryData = () => {
    return {
      supplier: selectedSupplierPreview,
      storeName: formState.selectedStore.name || companyPreview,
      address: formState.selectedAddress.ship_to_address,
      category: formState.category.category_name,
      fulfillment: formState.fulfillment.fulfillment_name,
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div
        className="max-w-full mx-
      px-3 md:px-20 py-8"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create New Order
          </h1>
          <p className="text-gray-600">Follow the steps to place your order</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm font-medium text-gray-700">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-center mb-8 overflow-x-auto">
          <div className="flex items-center space-x-4">
            {visibleSteps.map((step, index) => {
              const isCompleted = index < activeStep;
              const isCurrent = index === activeStep && !isLastStep;
              const Icon = step.icon;

              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                      isCompleted
                        ? "bg-blue-500 border-blue-500 text-white"
                        : isCurrent
                        ? "border-blue-500 text-blue-500 bg-blue-50"
                        : "border-gray-300 text-gray-400 bg-white"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-6 w-6" />
                    ) : (
                      <Icon className="h-6 w-6" />
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <div
                      className={`text-sm font-medium ${
                        isCompleted || isCurrent
                          ? "text-gray-900"
                          : "text-gray-500"
                      }`}
                    >
                      {step.title}
                    </div>
                  </div>
                  {index < visibleSteps.length - 1 && (
                    <div
                      className={`w-8 h-0.5 mx-4 ${
                        isCompleted ? "bg-blue-500" : "bg-gray-300"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}

            {/* Final step indicator */}
            <div className="flex items-center">
              <div
                className={`w-8 h-0.5 mx-4 ${
                  isLastStep ? "bg-blue-500" : "bg-gray-300"
                }`}
              ></div>
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                  isLastStep
                    ? "border-blue-500 text-blue-500 bg-blue-50"
                    : "border-gray-300 text-gray-400 bg-white"
                }`}
              >
                <Truck className="h-6 w-6" />
              </div>
              <div className="ml-3 hidden sm:block">
                <div
                  className={`text-sm font-medium ${
                    isLastStep ? "text-gray-900" : "text-gray-500"
                  }`}
                >
                  Review & Order
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {isLastStep ? (
            // Summary and Product Selection
            <div className="p-8">
              {/* Summary Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Order Summary
                  </h2>
                  <button
                    onClick={handleReset}
                    className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors duration-200"
                  >
                    Reset & Start Over
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {getSummaryData().supplier && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Building className="h-5 w-5 text-gray-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Supplier
                          </div>
                          <div className="text-gray-900 font-semibold uppercase">
                            {getSummaryData().supplier}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {getSummaryData().storeName && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Building className="h-5 w-5 text-gray-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Store/Company
                          </div>
                          <div className="text-gray-900 font-semibold uppercase">
                            {getSummaryData().storeName}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {getSummaryData().address && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-gray-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Delivery Address
                          </div>
                          <div className="text-gray-900 font-semibold uppercase">
                            {getSummaryData().address}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {getSummaryData().category && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-gray-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Category
                          </div>
                          <div className="text-gray-900 font-semibold capitalize">
                            {getSummaryData().category}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {getSummaryData().fulfillment && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-gray-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Fulfillment Method
                          </div>
                          <div className="text-gray-900 font-semibold capitalize">
                            {getSummaryData().fulfillment}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Selection */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Product Selection
                </h3>
                <div className="text-gray-600 text-sm mb-4">
                  Transfer all products you want to order
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div>
                    <StockOrderProductSelector
                      category_id={formState.category.category_id}
                      company_id={companyId}
                      supplier={formState.selectedSupplier}
                      selected_store={formState.selectedStore}
                      setRows={setProductData}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Step Content
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                  <currentStep.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {currentStep.title}
                </h2>
                <p className="text-gray-600">{currentStep.subtitle}</p>
              </div>

              {currentStepData && (
                <div className="max-w-md mx-auto">
                  {currentStepData.autoCompletePropSetup && (
                    <MaterialInputAutoComplete
                      colorTheme={"black"}
                      required
                      fullWidth
                      size={"medium"}
                      options={currentStepData.autoCompletePropSetup.options}
                      getOptionLabel={(option) =>
                        option?.[
                          currentStepData.autoCompletePropSetup.optionLabelName
                        ] || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.[
                          currentStepData.autoCompletePropSetup.optionLabelName
                        ] ===
                        value?.[
                          currentStepData.autoCompletePropSetup.optionLabelName
                        ]
                      }
                      onChange={currentStepData.autoCompletePropSetup.onChange}
                      placeholder={
                        currentStepData.autoCompletePropSetup.placeholder
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          "& fieldset": {
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#6B7280",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#3B82F6",
                          },
                        },
                      }}
                    />
                  )}

                  {currentStepData.poInfo && (
                    <div className="flex flex-col space-y-3">
                      <input
                        className="w-full border-2 rounded-xl border-[#3B82F6] hover:border-[#6B7280] p-2"
                        type="text"
                        maxLength={currentStepData.poInfo.poNumber.max}
                        placeholder={
                          currentStepData.poInfo.poNumber.placeholder
                        }
                        onChange={currentStepData.poInfo.poNumber.onChange}
                      />

                      <button
                        onClick={currentStepData.poInfo.uploadPo.onclick}
                        className=" text-[#3B82F6] hover:text-[#6B7280] w-full p-5 bg-gray-100 border-2 border-dashed border-[#3B82F6] hover:border-[#6B7280] rounded-xl"
                      >
                        <FileInput className="flex justify-center w-full" />

                        <span>
                          {formState.poInfo.uploadedPo?.length === 0
                            ? currentStepData.poInfo.uploadPo.btnName
                            : formState.poInfo.uploadedPo?.map(
                                (file) => file.name
                              )}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-gray-50 px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-200">
            {/* Mobile Layout - Stacked */}
            <div className="flex flex-col space-y-4 sm:hidden">
              {/* Step indicator at top on mobile */}
              <div className="text-center">
                <span className="text-sm text-gray-500">
                  Step {activeStep + 1} of {visibleSteps.length + 1}
                </span>
              </div>

              {/* Buttons row */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-5">
                  <button
                    onClick={handleBack}
                    className={`flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 ${
                      activeStep === 0 && "invisible"
                    }`}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span className="text-sm">Back</span>
                  </button>
                  {activeStep >= 3 &&
                    formState.poInfo.poNumber !== "" &&
                    formState.poInfo.uploadedPo?.length !== 0 && (
                      <button
                        onClick={handleNext}
                        className={`flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 ${
                          activeStep === 0 && "invisible"
                        }`}
                      >
                        <ArrowRight className="h-4 w-4" />
                        <span className="text-sm">Next</span>
                      </button>
                    )}
                </div>

                {isLastStep && productData.length > 0 && (
                  <>
                    {getAdminSessionState.data?.admin.user_details.company
                      .company_id === 1 ? (
                      // Stacked buttons for company_id 1 on mobile
                      <div className="flex flex-col space-y-2 w-full max-w-xs">
                        <button
                          onClick={handleConfirmOrder(true)}
                          className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2.5 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl text-sm w-full"
                        >
                          <span>Staggered</span>
                          <ArrowRight className="h-4 w-4" />
                        </button>

                        <button
                          onClick={handleConfirmOrder()}
                          className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl text-sm w-full"
                        >
                          <span>Full Order</span>
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      // Single button for other companies
                      <button
                        onClick={handleConfirmOrder()}
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2.5 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl text-sm"
                      >
                        <span>Continue</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Desktop Layout - Original */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex space-x-5">
                <button
                  onClick={handleBack}
                  className={`flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 ${
                    activeStep === 0 && "invisible"
                  }`}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </button>
                {activeStep >= 3 &&
                  formState.poInfo.poNumber !== "" &&
                  formState.poInfo.uploadedPo?.length !== 0 && (
                    <button
                      onClick={handleNext}
                      className={`flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 ${
                        activeStep === 4 && "invisible"
                      }`}
                    >
                      <ArrowRight className="h-4 w-4" />
                      <span>Next</span>
                    </button>
                  )}
              </div>

              <div className="text-sm text-gray-500">
                Step {activeStep + 1} of {visibleSteps.length + 1}
              </div>

              {isLastStep && productData.length > 0 && (
                <>
                  {getAdminSessionState.data?.admin.user_details.company
                    .company_id === 1 ? (
                    // Database driven for staggered button
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleConfirmOrder(true)}
                        className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                      >
                        <span>Staggered Setup</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>

                      <button
                        onClick={handleConfirmOrder()}
                        className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                      >
                        <span>Full Order Setup</span>
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    // Single button for other companies
                    <button
                      onClick={handleConfirmOrder()}
                      className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                    >
                      <span>Proceed to order confirmation</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <UploadMultipleImageModal
        open={openUploadModal}
        single
        onClose={() => {
          setOpenUploadModal(false);
          dispatch(closeModalScroll());
        }}
        files={formState.poInfo.uploadedPo}
        setFiles={(file) =>
          setFormState({
            ...formState,
            poInfo: { ...formState.poInfo, uploadedPo: file },
          })
        }
      />
    </div>
  );
}
